import { Heart, UserPlus, UserCheck } from "lucide-react";
import { GlassCard } from "@/components/GlassCard";
import { FeedCardMenu } from "@/components/community/FeedCardMenu";
import { useLanguage } from "@/i18n/LanguageContext";
import { pickMessage } from "@/lib/messages";
import { activityIcon } from "@/lib/constants";
import { cn } from "@/lib/utils";

const initials = (name) =>
  (name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "?";

const useTimeAgo = () => {
  const { t } = useLanguage();
  return (iso) => {
    const diffMinutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (diffMinutes < 1) return t("community.timeNow");
    if (diffMinutes < 60) return t("community.timeMin", { n: diffMinutes });
    const hours = Math.floor(diffMinutes / 60);
    if (hours < 24) return t("community.timeHours", { n: hours });
    return t("community.timeDays", { n: Math.floor(hours / 24) });
  };
};

export const FeedCard = ({ post, onKudos, onFollow, onBlocked }) => {
  const { t } = useLanguage();
  const timeAgo = useTimeAgo();
  const Icon = activityIcon(post.activity_type);
  const salt = post.id
    ? post.id.charCodeAt(post.id.length - 1) + post.duration_minutes
    : 0;
  const caption = post.caption || pickMessage(t("motionCard.sentencePool"), salt);

  return (
    <GlassCard className="p-4" data-testid="community-feed-card">
      {/* Header: author + follow */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 shrink-0 rounded-full bg-gradient-to-br from-cyan-300/30 to-teal-300/15 border border-cyan-300/30 flex items-center justify-center text-xs font-semibold text-cyan-100">
            {initials(post.author_name)}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold truncate">{post.author_name}</p>
              {post.is_demo && (
                <span
                  data-testid="community-demo-badge"
                  className="text-[9px] uppercase tracking-wider rounded-full bg-white/10 border border-white/10 px-1.5 py-0.5 text-[hsl(var(--muted-foreground))]"
                >
                  {t("community.demoBadge")}
                </span>
              )}
            </div>
            <p className="text-[11px] text-[hsl(var(--muted-foreground))]">
              {timeAgo(post.created_at)}
            </p>
          </div>
        </div>
        {!post.is_own && (
          <div className="shrink-0 flex items-center gap-1">
            <button
              data-testid={`community-follow-${post.profile_id}`}
              onClick={() => onFollow(post)}
              className={cn(
                "flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold border transition-colors duration-200 active:scale-[0.96]",
                post.is_following
                  ? "bg-cyan-300/15 border-cyan-300/40 text-cyan-200"
                  : "bg-white/5 border-white/10 text-white/80 hover:bg-white/10"
              )}
            >
              {post.is_following ? <UserCheck size={12} /> : <UserPlus size={12} />}
              {post.is_following ? t("community.following") : t("community.follow")}
            </button>
            {/* Phase 10 (UGC): report / block overflow menu */}
            <FeedCardMenu post={post} onBlocked={onBlocked} />
          </div>
        )}
      </div>

      {/* Body: activity + goal */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="flex items-center gap-1.5 rounded-full bg-cyan-300/10 border border-cyan-300/20 px-3 py-1 text-xs font-medium text-cyan-100">
          <Icon size={13} />
          {t(`types.${post.activity_type}`)} · {post.duration_minutes} {t("common.min")}
        </span>
        <span className="rounded-full bg-white/5 border border-white/10 px-3 py-1 text-xs text-[hsl(var(--muted-foreground))]">
          {t("motionCard.goalLabel")}: {t(`goals.${post.goal}`)}
        </span>
      </div>

      <p className="mt-3 text-sm leading-relaxed text-white/85">{caption}</p>

      {/* Footer: kudos */}
      <div className="mt-3 pt-3 border-t border-white/10 flex items-center">
        <button
          data-testid={`community-kudos-${post.id}`}
          aria-label={t("community.kudosAria")}
          onClick={() => onKudos(post)}
          className={cn(
            "flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors duration-200 active:scale-[0.95]",
            post.has_kudos
              ? "bg-cyan-300/15 border-cyan-300/40 text-cyan-200"
              : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"
          )}
        >
          <Heart
            size={13}
            className={post.has_kudos ? "fill-cyan-300 text-cyan-300" : ""}
          />
          <span data-testid={`community-kudos-count-${post.id}`}>{post.kudos_count}</span>
        </button>
      </div>
    </GlassCard>
  );
};
